import type { PostedWithin, WorkSetting } from "@/lib/api/catalogue";
import { languageName, LANGUAGES } from "@/lib/languages";
import { formatMoney, WORK_SETTING_LABEL } from "@/lib/openings";
import { PAY_CURRENCY } from "@/lib/pay";
import { QUICK_LANGUAGES, WITHIN_CHIP, type WithinMiles } from "./view";

/**
 * What each filter's button says it holds, and null for a filter that holds
 * nothing, which shows its own name instead. Pure.
 */

/** What stands between two sums: an en dash, as the design has it. */
const RANGE_MARK = "–";

/** Under this, a sum could be taken for hourly or monthly pay, so the year is named. */
const PLAINLY_YEARLY = 1_000;

const POSTED_SUMMARY: Record<PostedWithin, string> = {
    "24h": "Past 24 hours",
    "3d": "Past 3 days",
    "7d": "Past week",
};

/** The first name and how many more: "English +2". */
function firstAndMore(names: readonly string[]): string | null {
    const [first, ...others] = names;
    if (first === undefined) return null;
    return others.length > 0 ? `${first} +${others.length}` : first;
}

export function settingsSummary(settings: readonly WorkSetting[]): string | null {
    return firstAndMore(settings.map((setting) => WORK_SETTING_LABEL[setting]));
}

export function postedSummary(posted: PostedWithin | null): string | null {
    return posted && POSTED_SUMMARY[posted];
}

export function withinSummary(within: WithinMiles | null): string | null {
    return within === null ? null : `Within ${WITHIN_CHIP[within]}`;
}

export function languagesSummary(languages: readonly string[]): string | null {
    return firstAndMore(languages.map(languageName));
}

/** A yearly range in short: "$65k–$125k", "$65k+", "Up to $125k". Either end may be open. */
export function paySummary(lower: number | null, upper: number | null): string | null {
    const highest = upper ?? lower;
    if (highest === null) return null;
    const period = highest < PLAINLY_YEARLY ? "/yr" : "";
    const money = (amount: number) => formatMoney(amount, PAY_CURRENCY, true);
    if (upper === null) return `${money(highest)}+${period}`;
    if (lower === null) return `Up to ${money(upper)}${period}`;
    if (lower === upper) return `${money(upper)}${period}`;
    return `${money(lower)}${RANGE_MARK}${money(upper)}${period}`;
}

/**
 * The languages under More languages: any picked that the page does not
 * offer, such as one the browser asked for, then the page's own. Their order
 * does not follow the picks, so a row never moves from under the pointer.
 */
export function moreLanguages(languages: readonly string[]): string[] {
    const quick = QUICK_LANGUAGES.map(([code]) => code);
    return [
        ...languages.filter((code) => !quick.includes(code) && !LANGUAGES.includes(code)),
        ...LANGUAGES.filter((code) => !quick.includes(code)),
    ];
}
